import React, { useEffect, useState } from "react";
import axios from "axios";
import { Edit, Trash2, RotateCcw, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./JobCategoryList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const JobCategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const endpoint = showInactive
        ? `${url}/api/jobcategory/all`
        : `${url}/api/jobcategory`;
      const res = await axios.get(endpoint);
      setCategories(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch job categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [showInactive]);

  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this job category?")) {
      try {
        const res = await axios.delete(`${url}/api/jobcategory/${id}`);
        toast.success(res.data.message || "Job category deactivated successfully");
        fetchCategories();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to deactivate job category");
      }
    }
  };

  const handleRestore = async (id) => {
    if (window.confirm("Are you sure you want to activate this job category?")) {
      try {
        const res = await axios.put(`${url}/api/jobcategory/${id}/restore`);
        toast.success(res.data.message || "Job category activated successfully");
        fetchCategories();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to activate job category");
      }
    }
  };

  if (loading) return <div className="loading">Loading job categories...</div>;

  return (
    <div className="jobcategory-container">
      <div className="header">
        <h2>Job Categories List</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`toggle-button ${showInactive ? 'active' : ''}`}
          >
            {showInactive ? 'Showing All' : 'Show Active Only'}
          </button>
          <button
            onClick={() => navigate("/AddJobcategory")}
            className="add-button"
          >
            <Plus size={16} /> Add Category
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="jobcategory-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Category Name</th>
              <th>Job Description</th>
              <th>Status</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  No job categories found
                </td>
              </tr>
            ) : (
              categories.map((cat) => (
                <tr key={cat._id} className={cat.status === 'inactive' ? 'inactive-row' : ''}>
                  <td>{cat.no}</td>
                  <td className="category-name">{cat.category_name}</td>
                  <td>{cat.description || "N/A"}</td>
                  <td>
                    <span className={`status-badge ${cat.status}`}>
                      {cat.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <div className="actions">
                      {cat.status === 'active' ? (
                        <>
                          <button
                            onClick={() => navigate(`/jobcategory/edit/${cat._id}`)}
                            className="edit"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleSoftDelete(cat._id)}
                            className="delete"
                            title="Deactivate"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestore(cat._id)}
                          className="restore-btn"
                          title="Activate"
                        >
                          <RotateCcw size={16} /> Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobCategoryList;